import React from "react";

// The "page" under the hero lens: text adapted from 26 U.S.C. § 42 (public
// domain), plus JPRO's annotations on a sample property. Rendered twice by
// Hero: once plain (the fine print) and once annotated (what the lens shows).
// Both renders must produce identical text flow so the layers line up, so
// annotations are absolutely positioned and never take up layout space.

function Flag({ annotated, clause, note, children }) {
    return (
        <span data-clause={clause} className="relative">
            <span
                className={
                    annotated
                        ? "text-ink dark:text-paper underline decoration-stamp decoration-[1.5px] underline-offset-[3px]"
                        : ""
                }
            >
                {children}
            </span>
            {annotated && note && (
                <span className="absolute left-0 bottom-full mb-0.5 whitespace-nowrap bg-paper px-1 font-plex-mono text-[9.5px] not-italic leading-[1.3] tracking-normal text-stamp dark:bg-ink-950">
                    {note}
                </span>
            )}
        </span>
    );
}

function Circled({ annotated, children }) {
    return (
        <span className="relative">
            {children}
            {annotated && (
                <svg
                    aria-hidden="true"
                    viewBox="0 0 100 40"
                    preserveAspectRatio="none"
                    className="absolute -left-[14%] -top-[45%] h-[190%] w-[128%] overflow-visible text-stamp"
                >
                    {/* Hand-drawn ellipse: overshoots its start like a pen stroke */}
                    <path
                        d="M8 22 C 6 8, 40 2, 66 5 C 92 8, 98 20, 90 30 C 80 39, 30 40, 12 31 C 2 25, 10 12, 30 8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                    />
                </svg>
            )}
        </span>
    );
}

function FinePrint({ annotated = false }) {
    const a = annotated;
    return (
        <div
            className={`font-news text-[12.5px] leading-[1.75] text-justify hyphens-auto columns-1 sm:columns-2 gap-8 [column-rule:1px_solid_rgba(22,24,29,0.12)] dark:[column-rule:1px_solid_rgba(236,231,220,0.12)] ${
                a ? "text-ink/80 dark:text-paper/85" : "text-ink/35 dark:text-paper/30"
            }`}
        >
            <p className="mb-3">
                <span className="font-plex-mono text-[10.5px] uppercase tracking-[0.12em]">(g) Qualified low-income housing project. </span>
                (1) In general. The term &ldquo;qualified low-income housing project&rdquo; means any project for
                residential rental property if the project meets the requirements of subparagraph (A), (B), or (C),
                whichever is elected by the taxpayer. (A) 20-50 test. The project meets the requirements of this
                subparagraph if{" "}
                <Flag annotated={a} clause="§42(g)(1)(A)" note="Election on file: 40-60, not 20-50">
                    20 percent or more of the residential units
                </Flag>{" "}
                in such project are both rent-restricted and occupied by individuals whose income is 50 percent or
                less of area median gross income. (B) 40-60 test. The project meets the requirements of this
                subparagraph if 40 percent or more of the residential units in such project are both rent-restricted
                and{" "}
                <Flag annotated={a} clause="§42(g)(1)(B)" note="3 households within 5% of limit at recert">
                    occupied by individuals whose income is 60 percent or less
                </Flag>{" "}
                of area median gross income.
            </p>
            <p className="mb-3">
                (C) Average income test. (i) In general. The project meets the minimum requirements of this
                subparagraph if 40 percent or more of the residential units in such project are both rent-restricted
                and occupied by individuals whose income does not exceed the imputed income limitation designated by
                the taxpayer with respect to the respective unit. (ii) Special rules relating to income limitation.
                (I) Designation. The taxpayer shall designate the imputed income limitation of each unit taken into
                account under clause (i). (II) Average test. The average of the imputed income limitations designated
                under subclause (I){" "}
                <Flag annotated={a} clause="§42(g)(1)(C)(ii)(II)" note="Avg designation 59.6%  OK">
                    shall not exceed <Circled annotated={a}>60 percent</Circled> of area median gross income
                </Flag>
                . (III) 10-percent increments. The designated imputed income limitation of any unit under subclause
                (I) shall be{" "}
                <Flag annotated={a} clause="§42(g)(1)(C)(ii)(III)" note="Unit 4B at 80% would lift avg to 60.4%">
                    20 percent, 30 percent, 40 percent, 50 percent, 60 percent, 70 percent, or 80 percent
                </Flag>{" "}
                of area median gross income.
            </p>
            <p className="mb-3">
                <span className="font-plex-mono text-[10.5px] uppercase tracking-[0.12em]">(2) Rent-restricted units. </span>
                (A) In general. For purposes of paragraph (1), a residential unit is rent-restricted if the gross rent
                with respect to such unit{" "}
                <Flag annotated={a} clause="§42(g)(2)(A)" note="Unit 2C: $14 over after utility allowance update">
                    does not exceed 30 percent of the imputed income limitation
                </Flag>{" "}
                applicable to such unit. For purposes of the preceding sentence, the amount of the income limitation
                under paragraph (1) applicable for any period shall not be less than such limitation applicable for
                the earliest period the building (which contains the unit) was included in the determination of
                whether the project is a qualified low-income housing project. (B) Gross rent. For purposes of
                subparagraph (A), gross rent includes any utility allowance determined by the Secretary after taking
                into account such determinations under section 8 of the United States Housing Act of 1937.
            </p>
            <p className="mb-3">
                (D) Treatment of units occupied by individuals whose incomes rise above limit. (i) In general.
                Notwithstanding an increase in the income of the occupants of a low-income unit above the income
                limitation applicable under paragraph (1), such unit shall continue to be treated as a low-income unit
                if the income of such occupants initially met such income limitation and such unit continues to be
                rent-restricted. (ii) Next available unit must be rented to low-income tenant if income rises above{" "}
                <Flag annotated={a} clause="§42(g)(2)(D)(ii)" note="Household 3A at 146%: next available unit rule">
                    140 percent of income limitation
                </Flag>
                . If the income of the occupants of the unit increases above 140 percent of the income limitation
                applicable under paragraph (1), clause (i) shall cease to apply to any such unit if any residential
                rental unit in the building (of a size comparable to, or smaller than, such unit) is occupied by a new
                resident whose income exceeds such income limitation.
            </p>
            <p className="mb-3">
                <span className="font-plex-mono text-[10.5px] uppercase tracking-[0.12em]">(i) Definitions and special rules. </span>
                (1) Compliance period. The term &ldquo;compliance period&rdquo; means, with respect to any building,
                the{" "}
                <Flag annotated={a} clause="§42(i)(1)" note="Sample property: year 7 of 15">
                    period of 15 taxable years
                </Flag>{" "}
                beginning with the 1st taxable year of the credit period with respect thereto. (3) Low-income unit.
                (A) In general. The term &ldquo;low-income unit&rdquo; means any unit in a building if such unit is
                rent-restricted, and the individuals occupying such unit meet the income limitation applicable under
                subsection (g)(1) to the project of which such building is a part.
            </p>
            <p className="mb-3">
                (B) Exceptions. A unit shall not be treated as a low-income unit unless the unit is suitable for
                occupancy and used other than on a transient basis. (C) Suitability for occupancy. For purposes of
                subparagraph (B), the suitability of a unit for occupancy shall be determined under regulations
                prescribed by the Secretary taking into account local health, safety, and building codes. (D)
                Transitional housing for homeless. For purposes of subparagraph (B), a unit shall be considered to be
                used other than on a transient basis if the unit contains sleeping accommodations and kitchen and
                bathroom facilities and is located in a building which is used exclusively to facilitate the
                transition of homeless individuals to independent living within 24 months.
            </p>
            <p>
                (g)(3) Date for meeting requirements. (A) In general. Except as otherwise provided in this paragraph, a
                building shall be treated as a qualified low-income building only if the project (of which such
                building is a part) meets the requirements of paragraph (1) not later than the close of the 1st year
                of the credit period for such building. (B) Buildings which rely on later buildings for
                qualification. In determining whether a building (hereinafter in this subparagraph referred to as the
                &ldquo;prior building&rdquo;) is a qualified low-income building, the taxpayer may take into account
                1 or more additional buildings placed in service during the 12-month period described in subparagraph
                (A) with respect to the prior building only if the taxpayer elects to apply this subparagraph.
            </p>
        </div>
    );
}

export default FinePrint;
